import { useEffect, useRef } from 'react'
import { useSnapshot } from 'valtio'
import { handleMobileButtonActionCommand, handleMobileButtonLongPress } from '../controls'
import { watchValue } from '../optionsStorage'
import { type MobileButtonConfig, type ActionHoldConfig, type ActionType, type CustomAction } from '../appConfig'
import { miscUiState } from '../globalState'
import PixelartIcon from './PixelartIcon'
import { packetsReplayState } from './state/packetsReplayState'
import { ChatFilledIcon, MenuSettingsIcon } from './components/replay/PlayerIcons'
import styles from './MobileTopButtons.module.css'

/** Actions whose config entry carries no label — they were gui.png sprites. */
const ACTION_ICON: Record<string, () => JSX.Element> = {
  'general.chat': () => <ChatFilledIcon size={16} />,
  'ui.pauseMenu': () => <MenuSettingsIcon size={16} />,
}

export default () => {
  const elRef = useRef<HTMLDivElement | null>(null)
  const { appConfig } = useSnapshot(miscUiState)
  const { isOpen: replayOpen } = useSnapshot(packetsReplayState)
  const mobileButtonsConfig = appConfig?.mobileButtons

  const longPressTimerIdRef = useRef<number | null>(null)
  const actionToShortPressRef = useRef<ActionType | null>(null)

  const showMobileControls = (visible: boolean) => {
    if (elRef.current) {
      elRef.current.style.display = visible ? 'flex' : 'none'
    }
  }

  useEffect(() => {
    watchValue(miscUiState, o => {
      showMobileControls(Boolean(o.currentTouch))
    })
  }, [])

  const getButtonClassName = (button: MobileButtonConfig): string => {
    const actionForStyle = button.action || (button.actionHold && typeof button.actionHold === 'object' && 'command' in button.actionHold ? button.actionHold.command : undefined)

    if (typeof actionForStyle === 'string') {
      switch (actionForStyle) {
        case 'general.chat':
          return styles['chat-btn']
        case 'ui.pauseMenu':
          return styles['pause-btn']
        case 'general.playersList':
          return styles['tab-btn']
        default:
          return styles['debug-btn']
      }
    }
    return styles['debug-btn']
  }

  const renderConfigButtons = () => {
    return mobileButtonsConfig?.map((button, index) => {
      const className = getButtonClassName(button)
      let label: string | JSX.Element = button.icon || button.label || ''

      if (typeof label === 'string' && label.startsWith('pixelarticons:')) {
        const iconName = label.replace('pixelarticons:', '')
        label = <PixelartIcon iconName={iconName} />
      }
      if (!label && typeof button.action === 'string') {
        label = ACTION_ICON[button.action]?.() ?? ''
      }

      const onPointerDown = (e: React.PointerEvent) => {
        const elem = e.currentTarget as HTMLElement
        elem.setPointerCapture(e.pointerId)

        if (longPressTimerIdRef.current) {
          clearTimeout(longPressTimerIdRef.current)
          longPressTimerIdRef.current = null
        }
        actionToShortPressRef.current = null

        const { actionHold, action } = button

        if (actionHold) {
          if (typeof actionHold === 'object' && 'command' in actionHold) {
            const config = actionHold
            if (config.longPressAction) {
              actionToShortPressRef.current = config.command
              longPressTimerIdRef.current = window.setTimeout(() => {
                handleMobileButtonLongPress(config)
                actionToShortPressRef.current = null
                longPressTimerIdRef.current = null
              }, config.duration || 500)
            } else {
              handleMobileButtonActionCommand(config.command, true)
            }
          } else if (action) {
            actionToShortPressRef.current = action
            longPressTimerIdRef.current = window.setTimeout(() => {
              handleMobileButtonActionCommand(actionHold, true)
              actionToShortPressRef.current = null
              longPressTimerIdRef.current = null
            }, 500)
          } else {
            handleMobileButtonActionCommand(actionHold, true)
          }
        } else if (action) {
          handleMobileButtonActionCommand(action, true)
        }
      }

      const onPointerUp = (e: React.PointerEvent) => {
        const elem = e.currentTarget as HTMLElement
        elem.releasePointerCapture(e.pointerId)

        const { actionHold, action } = button
        let wasShortPressHandled = false

        if (longPressTimerIdRef.current) {
          clearTimeout(longPressTimerIdRef.current)
          longPressTimerIdRef.current = null
          if (actionToShortPressRef.current) {
            handleMobileButtonActionCommand(actionToShortPressRef.current, true)
            handleMobileButtonActionCommand(actionToShortPressRef.current, false)
            wasShortPressHandled = true
          }
        }

        if (!wasShortPressHandled) {
          if (actionHold) {
            if (typeof actionHold === 'object' && 'command' in actionHold) {
              const config = actionHold
              if (config.longPressAction) {
                if (actionToShortPressRef.current === null) {
                  if (typeof config.longPressAction === 'string') {
                    handleMobileButtonActionCommand(config.longPressAction, false)
                  }
                }
              } else {
                handleMobileButtonActionCommand(config.command, false)
              }
            } else if (action) {
              if (actionToShortPressRef.current === null) {
                handleMobileButtonActionCommand(actionHold, false)
              }
            } else {
              handleMobileButtonActionCommand(actionHold, false)
            }
          } else if (action) {
            handleMobileButtonActionCommand(action, false)
          }
        }
        actionToShortPressRef.current = null
      }

      return (
        <div
          key={index}
          className={className}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onLostPointerCapture={onPointerUp}
        >
          {label}
        </div>
      )
    })
  }

  // In the replay player the top-centre row belongs to TopMenu.
  if (replayOpen) return null

  // ios note: just don't use <button>
  return (
    <div ref={elRef} className={styles['mobile-top-btns']} id="mobile-top">
      {mobileButtonsConfig && mobileButtonsConfig.length > 0 ? renderConfigButtons() : null}
    </div>
  )
}
